"use client"

import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useHistory } from "react-router-dom"
import { useAuth0 } from "@auth0/auth0-react"
import {
  ArrowLeft, CalendarDays, CheckCircle2, CircleHelp, FilePenLine, ListChecks,
  MessageCircleQuestion, Plus, Sparkles, ThumbsDown, ThumbsUp,
} from "lucide-react"
import { getApiBaseUrl } from "../../config/apiBase"
import RefinementGuide from "./RefinementGuide"
import GenericRefinementDiscussion from "./refinement-discussion"
import "../../styles/components/GenericRefinementWorkspace.css"

export const SUGGESTION_KINDS = {
  task: {
    label: "Task",
    icon: ListChecks,
    prompt: "A concrete piece of work someone can take responsibility for.",
    outcome: "If supported, it becomes Taskboard work only when the group approves the complete refined plan.",
  },
  calendar_event: {
    label: "Calendar event",
    icon: CalendarDays,
    prompt: "A date, gathering, deadline, or milestone that belongs on the shared calendar.",
    outcome: "If supported, it is added to the group calendar when the complete refined plan passes.",
  },
  field_edit: {
    label: "Plan edit",
    icon: FilePenLine,
    prompt: "Replacement wording, a corrected date, cost, role, or another specific field.",
    outcome: "Shows the exact part of the plan members are being asked to change.",
  },
  detail: {
    label: "Added detail",
    icon: Plus,
    prompt: "Useful context, a safeguard, requirement, resource, or success measure.",
    outcome: "Adds clarity the final plan should retain without replacing the original idea.",
  },
  question: {
    label: "Open question",
    icon: MessageCircleQuestion,
    prompt: "Something the group must answer before the plan is ready to carry out.",
    outcome: "Keeps an unresolved decision visible so it is not mistaken for an approved detail.",
  },
}

export default function GenericRefinementWorkspace({
  type,
  itemId,
  itemLabel,
  item,
  title,
  currentFields = [],
  fieldOptions = [],
}) {
  const history = useHistory()
  const { getAccessTokenSilently } = useAuth0()
  const [suggestions, setSuggestions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [notice, setNotice] = useState("")
  const [showComposer, setShowComposer] = useState(false)
  const [kind, setKind] = useState("task")
  const [field, setField] = useState(fieldOptions[0]?.value || "Plan details")
  const [suggestionTitle, setSuggestionTitle] = useState("")
  const [content, setContent] = useState("")
  const [saving, setSaving] = useState(false)
  const [isBlocking, setIsBlocking] = useState(true)
  const [eventStart, setEventStart] = useState("")
  const [eventEnd, setEventEnd] = useState("")
  const [eventLocation, setEventLocation] = useState("")
  const [approval, setApproval] = useState(null)
  const [showReview, setShowReview] = useState(false)
  const [approvalSaving, setApprovalSaving] = useState(false)
  const [resolvingId, setResolvingId] = useState(null)
  const [resolution, setResolution] = useState("")

  const apiRoot = `${getApiBaseUrl()}/api/refinement/${type}/${itemId}`
  const accessToken = useCallback(() => getAccessTokenSilently({
    authorizationParams: { audience: process.env.REACT_APP_API_AUDIENCE },
  }), [getAccessTokenSilently])

  const fetchSuggestions = useCallback(async () => {
    try {
      setLoading(true)
      setError("")
      const token = await accessToken()
      const response = await fetch(apiRoot, { headers: { Authorization: `Bearer ${token}` } })
      if (!response.ok) throw new Error(`Suggestions could not be loaded (${response.status})`)
      const data = await response.json()
      setSuggestions(Array.isArray(data) ? data : [])
      const approvalResponse = await fetch(`${apiRoot}/approval-status`, { headers: { Authorization: `Bearer ${token}` } })
      if (!approvalResponse.ok) throw new Error(`Plan approval status could not be loaded (${approvalResponse.status})`)
      setApproval(await approvalResponse.json())
    } catch (requestError) {
      setError(requestError.message || "Suggestions could not be loaded.")
    } finally {
      setLoading(false)
    }
  }, [accessToken, apiRoot])

  useEffect(() => { fetchSuggestions() }, [fetchSuggestions])

  const progress = useMemo(() => ({
    tasks: suggestions.filter(suggestion => suggestion.kind === "task").length,
    events: suggestions.filter(suggestion => suggestion.kind === "calendar_event").length,
    edits: suggestions.filter(suggestion => suggestion.kind === "field_edit").length,
    details: suggestions.filter(suggestion => suggestion.kind === "detail").length,
    questions: suggestions.filter(suggestion => suggestion.kind === "question").length,
  }), [suggestions])
  const supportedSuggestions = useMemo(() => suggestions.filter(suggestion =>
    (suggestion.upvotes || 0) > (suggestion.downvotes || 0)), [suggestions])
  const planReady = useMemo(() => supportedSuggestions.filter(suggestion => suggestion.kind !== "question"), [supportedSuggestions])
  const openQuestions = useMemo(() => suggestions.filter(suggestion =>
    suggestion.kind === "question" && !suggestion.resolutionText), [suggestions])

  const postSuggestion = async (payload) => {
    const token = await accessToken()
    const response = await fetch(apiRoot, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload),
    })
    if (!response.ok) throw new Error(`Suggestion could not be posted (${response.status})`)
  }

  const submitManual = async () => {
    if (!content.trim() || !suggestionTitle.trim() || saving || (kind === "calendar_event" && !eventStart)) return
    setSaving(true)
    setError("")
    try {
      const suggestionContent = kind === "calendar_event"
        ? JSON.stringify({ title: suggestionTitle, description: content, startDateTime: eventStart, endDateTime: eventEnd, location: eventLocation })
        : content
      await postSuggestion({ kind, field: kind === "field_edit" ? field : "Plan details", title: suggestionTitle, content: suggestionContent, isBlocking: kind === "question" ? isBlocking : false })
      setContent("")
      setSuggestionTitle("")
      setEventStart("")
      setEventEnd("")
      setEventLocation("")
      setShowComposer(false)
      setNotice("Suggestion posted for the group to consider.")
      await fetchSuggestions()
    } catch (requestError) {
      setError(requestError.message || "Suggestion could not be posted.")
    } finally {
      setSaving(false)
    }
  }

  const vote = async (suggestionId, isUpvote) => {
    try {
      setError("")
      const token = await accessToken()
      const response = await fetch(`${apiRoot}/suggestions/${suggestionId}/vote`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ isUpvote }),
      })
      if (!response.ok) throw new Error(`Vote could not be saved (${response.status})`)
      const updated = await response.json()
      setSuggestions(current => current.map(suggestion => suggestion.id === suggestionId ? updated : suggestion))
      await fetchSuggestions()
    } catch (requestError) {
      setError(requestError.message || "Vote could not be saved.")
    }
  }

  const approvePlan = async () => {
    if (approvalSaving) return
    try {
      setError("")
      setApprovalSaving(true)
      const token = await accessToken()
      const response = await fetch(`${apiRoot}/approve`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      })
      const body = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(body.detail || "The refined plan could not be approved.")
      setApproval(body)
      setShowReview(false)
      setNotice(body.isFinalized ? "The refined plan passed. Its tasks are now on the Taskboard and the approved version is locked." : `Your approval of plan version ${body.version} was recorded.`)
      await fetchSuggestions()
    } catch (requestError) {
      setError(requestError.message || "The refined plan could not be approved.")
    } finally {
      setApprovalSaving(false)
    }
  }

  const withdrawApproval = async () => {
    if (approvalSaving) return
    try {
      setError("")
      setApprovalSaving(true)
      const token = await accessToken()
      const response = await fetch(`${apiRoot}/approval`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } })
      const body = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(body.detail || "Your approval could not be withdrawn.")
      setApproval(body)
      setNotice("Your approval was withdrawn. You can keep refining or approve this version again later.")
    } catch (requestError) {
      setError(requestError.message || "Your approval could not be withdrawn.")
    } finally {
      setApprovalSaving(false)
    }
  }

  const resolveQuestion = async (suggestionId) => {
    if (!resolution.trim() || saving) return
    try {
      setSaving(true)
      setError("")
      const token = await accessToken()
      const response = await fetch(`${apiRoot}/suggestions/${suggestionId}/resolve`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ resolution }),
      })
      const body = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(body.detail || "The answer could not be saved.")
      setResolution("")
      setResolvingId(null)
      setNotice("The question has been answered in the working plan.")
      await fetchSuggestions()
    } catch (requestError) {
      setError(requestError.message || "The answer could not be saved.")
    } finally {
      setSaving(false)
    }
  }

  return (
    <main className="plan-refinement">
      <header className="plan-refinement__hero">
        <button type="button" className="plan-refinement__back" onClick={() => history.goBack()}><ArrowLeft size={16} /> Back to group</button>
        <span className="plan-refinement__eyebrow"><Sparkles size={15} /> Build the workable plan</span>
        <h1>Refine {title || `this ${itemLabel.toLowerCase()}`}</h1>
        <p>The idea has support. Now help the group make it specific enough that people know what will change, what must be decided, and what work needs an owner.</p>
      </header>

      <section className="plan-refinement__orientation" aria-label="How refinement works">
        {[
          ["1", "Understand", "Read the approved idea and notice what is missing."],
          ["2", "Suggest", "Offer one task, calendar event, edit, detail, or open question at a time."],
          ["3", "Discern", "Support useful suggestions or oppose them with a reason."],
          ["4", "Approve", "Review the whole assembled plan. When the group threshold is reached, supported tasks move to the Taskboard."],
        ].map(([number, heading, copy]) => (
          <article key={number}><span>{number}</span><div><strong>{heading}</strong><p>{copy}</p></div></article>
        ))}
      </section>

      <section className="plan-refinement__current">
        <div className="plan-refinement__section-heading">
          <span>Approved starting point</span>
          <h2>What the group has already agreed to explore</h2>
        </div>
        <div className="plan-refinement__current-grid">
          {currentFields.map(current => (
            <article key={current.label}>
              <strong>{current.label}</strong>
              <p>{current.value || current.emptyText || "Still needs to be defined during refinement."}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="plan-refinement__kinds">
        <div className="plan-refinement__section-heading">
          <span>What can I contribute?</span>
          <h2>Choose the kind of suggestion you are making</h2>
          <p>Keep each suggestion focused. If your thought contains several jobs, make each job its own task so different people can take responsibility.</p>
        </div>
        <div className="plan-refinement__kind-grid">
          {Object.entries(SUGGESTION_KINDS).map(([value, config]) => {
            const Icon = config.icon
            return <article key={value}><Icon /><strong>{config.label}</strong><p>{config.prompt}</p><small>{config.outcome}</small></article>
          })}
        </div>
      </section>

      <RefinementGuide
        itemType={type}
        itemId={itemId}
        itemTitle={title || itemLabel}
        currentItem={item}
        fieldOptions={fieldOptions}
        onSuggestionPosted={fetchSuggestions}
        disabled={approval?.isFinalized}
      />

      <section className="plan-refinement__suggestions">
        <div className="plan-refinement__suggestions-header">
          <div className="plan-refinement__section-heading">
            <span>Working plan</span>
            <h2>Suggestions before the group</h2>
            <p>Vote on the result you want included—not merely whether the wording sounds good.</p>
          </div>
          {!approval?.isFinalized && <button type="button" onClick={() => setShowComposer(value => !value)}><Plus size={16} /> Add without the Guide</button>}
        </div>

        <div className="plan-refinement__summary" aria-label="Suggestion counts">
          <span>{progress.tasks} tasks</span><span>{progress.events} events</span><span>{progress.edits} edits</span><span>{progress.details} details</span><span>{progress.questions} questions</span>
        </div>

        <div className="plan-refinement__assembled">
          <div>
            <span>Refined plan so far</span>
            <strong>{planReady.length} supported plan item{planReady.length === 1 ? "" : "s"}</strong>
            <p>Items appear here once support exceeds opposition. Task proposals remain proposals until the complete refined plan passes.</p>
          </div>
          <div>
            <span>Still to resolve</span>
            <strong>{openQuestions.length} open question{openQuestions.length === 1 ? "" : "s"}</strong>
            <p>Answer these with a focused edit, detail, or task proposal. That keeps uncertainty separate from agreed work.</p>
          </div>
        </div>

        {approval && (
          <section className={`plan-refinement__approval${approval.isFinalized ? " is-final" : ""}`} aria-label="Approve the refined plan">
            <div className="plan-refinement__approval-heading">
              <div>
                <span>{approval.isFinalized ? "Approved final plan" : `Plan version ${approval.version}`}</span>
                <h2>{approval.isFinalized ? "This plan is ready for work" : "Does this complete plan feel ready?"}</h2>
                <p>{approval.isFinalized
                  ? `The group approved this exact version. ${approval.includedTasks} task${approval.includedTasks === 1 ? "" : "s"} moved to the Taskboard and ${approval.includedCalendarEvents || 0} event${approval.includedCalendarEvents === 1 ? "" : "s"} moved to the calendar.`
                  : `You are approving the original idea together with ${approval.includedEdits} supported edit${approval.includedEdits === 1 ? "" : "s"}, ${approval.includedDetails} added detail${approval.includedDetails === 1 ? "" : "s"}, ${approval.includedCalendarEvents || 0} calendar event${approval.includedCalendarEvents === 1 ? "" : "s"}, and ${approval.includedTasks} task proposal${approval.includedTasks === 1 ? "" : "s"}.`}</p>
              </div>
              <div className="plan-refinement__approval-count"><strong>{approval.approvalCount} / {approval.requiredApprovalCount}</strong><span>approvals toward passage</span><small>{approval.approvalPercent}% of {approval.eligibleMemberCount} eligible member{approval.eligibleMemberCount === 1 ? "" : "s"}</small></div>
            </div>
            {!approval.isFinalized && approval.openBlockingQuestions > 0 && <p className="plan-refinement__blocker"><CircleHelp size={17} /> {approval.openBlockingQuestions} supported blocking question{approval.openBlockingQuestions === 1 ? " must" : "s must"} be answered before this plan can pass.</p>}
            {!approval.isFinalized && !showReview && (
              <div className="plan-refinement__approval-actions">
                {approval.myApproval
                  ? approval.approvalCount >= approval.requiredApprovalCount
                    ? <><span>Your approval was recorded. Finalization still needs to finish.</span><button type="button" onClick={approvePlan} disabled={approvalSaving}>{approvalSaving ? "Finishing…" : "Finish approval"}</button></>
                    : <><span>You approved this exact version.</span><button type="button" className="is-secondary" onClick={withdrawApproval} disabled={approvalSaving}>Withdraw my approval</button></>
                  : <button type="button" onClick={() => setShowReview(true)} disabled={approval.openBlockingQuestions > 0}>Review and approve this refined plan</button>}
              </div>
            )}
            {!approval.isFinalized && showReview && (
              <div className="plan-refinement__review">
                <h3>You are approving the whole assembled plan</h3>
                <p>Your approval applies only to version {approval.version}. If the group changes which suggestions are supported, this approval stops counting until you review and approve the new version.</p>
                <div className="plan-refinement__review-list">
                  <span><strong>Original idea</strong> remains the foundation</span>
                  {supportedSuggestions.map(suggestion => <span key={suggestion.id}><strong>{SUGGESTION_KINDS[suggestion.kind]?.label || "Suggestion"}:</strong> {suggestion.title}{suggestion.resolutionText ? ` — ${suggestion.resolutionText}` : ""}</span>)}
                </div>
                <p><strong>When the threshold is reached:</strong> this version is locked, supported edits become final, and supported task proposals are created on the Taskboard.</p>
                <div><button type="button" className="is-secondary" onClick={() => setShowReview(false)}>Keep refining</button><button type="button" onClick={approvePlan} disabled={approvalSaving || approval.openBlockingQuestions > 0}>{approvalSaving ? "Approving…" : `Approve version ${approval.version}`}</button></div>
              </div>
            )}
          </section>
        )}

        {showComposer && (
          <div className="plan-refinement__composer">
            <div className="plan-refinement__type-picker">
              {Object.entries(SUGGESTION_KINDS).map(([value, config]) => (
                <button type="button" key={value} className={kind === value ? "is-selected" : ""} onClick={() => setKind(value)}>{config.label}</button>
              ))}
            </div>
            {kind === "field_edit" && <label>Which part changes?<select value={field} onChange={event => setField(event.target.value)}>{fieldOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>}
            <label>Short title<input value={suggestionTitle} onChange={event => setSuggestionTitle(event.target.value)} placeholder={kind === "task" ? "Arrange the baptism pool" : "Clarify the event time"} /></label>
            {kind === "calendar_event" && <div className="plan-refinement__event-fields"><label>Starts<input type="datetime-local" value={eventStart} onChange={event => setEventStart(event.target.value)} /></label><label>Ends<input type="datetime-local" value={eventEnd} min={eventStart} onChange={event => setEventEnd(event.target.value)} /></label><label>Place or meeting link<input value={eventLocation} onChange={event => setEventLocation(event.target.value)} placeholder="Fellowship hall or video link" /></label></div>}
            <label>{kind === "field_edit" ? "Proposed replacement" : "What are you proposing?"}<textarea rows={5} value={content} onChange={event => setContent(event.target.value)} placeholder="Make one focused suggestion and include the details another member would need to evaluate it." /></label>
            {kind === "question" && <label className="plan-refinement__blocking"><input type="checkbox" checked={isBlocking} onChange={event => setIsBlocking(event.target.checked)} /> This answer is required before the complete plan can pass</label>}
            <div><button type="button" className="is-secondary" onClick={() => setShowComposer(false)}>Cancel</button><button type="button" onClick={submitManual} disabled={saving || !content.trim() || !suggestionTitle.trim() || (kind === "calendar_event" && !eventStart)}>{saving ? "Posting…" : "Post suggestion"}</button></div>
          </div>
        )}

        {notice && <p className="plan-refinement__notice"><CheckCircle2 size={17} /> {notice}</p>}
        {error && <p className="plan-refinement__error" role="alert">{error}</p>}
        {loading ? <p className="plan-refinement__empty">Loading the working plan…</p> : suggestions.length === 0 ? (
          <div className="plan-refinement__empty"><CircleHelp /><h3>The plan is still open</h3><p>Use the Guide above or add one focused suggestion. A good first contribution is often a task that needs an owner or a question the group must answer.</p></div>
        ) : (
          <div className="plan-refinement__card-list">
            {suggestions.map(suggestion => {
              const config = SUGGESTION_KINDS[suggestion.kind] || SUGGESTION_KINDS.detail
              const Icon = config.icon
              const isSupported = (suggestion.upvotes || 0) > (suggestion.downvotes || 0)
              return (
                <article key={suggestion.id} className={`plan-suggestion is-${suggestion.kind}`}>
                  <div className="plan-suggestion__top">
                    <span className="plan-suggestion__kind"><Icon size={16} /> {config.label}</span>
                    {suggestion.convertedTaskId && <span className="plan-suggestion__done"><CheckCircle2 size={15} /> Created on Taskboard</span>}
                    {suggestion.convertedCalendarEventId && <span className="plan-suggestion__done"><CheckCircle2 size={15} /> Added to calendar</span>}
                    {!suggestion.convertedTaskId && isSupported && suggestion.kind !== "question" && <span className="plan-suggestion__done"><CheckCircle2 size={15} /> Supported for plan</span>}
                    {suggestion.kind === "question" && suggestion.isBlocking && !suggestion.resolutionText && <span className="plan-suggestion__blocking">Blocks final approval</span>}
                  </div>
                  <h3>{suggestion.title || config.label}</h3>
                  {suggestion.kind === "field_edit" && <p className="plan-suggestion__target">Changes: <strong>{suggestion.field}</strong></p>}
                  <p className="plan-suggestion__content">{suggestion.kind === "calendar_event" ? (() => { try { const event = JSON.parse(suggestion.content); return `${event.description || ""}${event.startDateTime ? ` · ${new Date(event.startDateTime).toLocaleString()}` : ""}${event.location ? ` · ${event.location}` : ""}` } catch { return suggestion.content } })() : suggestion.content}</p>
                  {suggestion.resolutionText && <p className="plan-suggestion__resolution"><strong>Answered:</strong> {suggestion.resolutionText}</p>}
                  <p className="plan-suggestion__outcome"><strong>What happens:</strong> {config.outcome}</p>
                  {suggestion.kind === "question" && !suggestion.resolutionText && resolvingId === suggestion.id && <div className="plan-suggestion__resolve"><label>Answer or decision<textarea rows={3} value={resolution} onChange={event => setResolution(event.target.value)} placeholder="Record the group’s answer clearly enough that it can guide the final plan." /></label><div><button type="button" className="is-secondary" onClick={() => { setResolvingId(null); setResolution("") }}>Cancel</button><button type="button" onClick={() => resolveQuestion(suggestion.id)} disabled={saving || !resolution.trim()}>Save answer</button></div></div>}
                  <div className="plan-suggestion__footer">
                    <span>Suggested by {suggestion.createdByName || "a group member"}</span>
                    <div className="plan-suggestion__actions">
                      <button type="button" disabled={approval?.isFinalized} className={suggestion.myVote === true ? "is-active is-support" : ""} onClick={() => vote(suggestion.id, true)} aria-label="Support suggestion"><ThumbsUp size={16} /> {suggestion.upvotes || 0}</button>
                      <button type="button" disabled={approval?.isFinalized} className={suggestion.myVote === false ? "is-active is-oppose" : ""} onClick={() => vote(suggestion.id, false)} aria-label="Oppose suggestion"><ThumbsDown size={16} /> {suggestion.downvotes || 0}</button>
                      {suggestion.kind === "question" && !suggestion.resolutionText && !approval?.isFinalized && <button type="button" className="is-convert" onClick={() => { setResolvingId(suggestion.id); setResolution("") }}>Answer this question</button>}
                    </div>
                  </div>
                </article>
              )
            })}
          </div>
        )}
      </section>

      <GenericRefinementDiscussion type={type} itemId={itemId} />
    </main>
  )
}
